// What the installed desktop app can do, by FEATURE DETECTION — never by
// version sniffing.  Desktop 1.0.13 timer bar (James, 2026-10-01).
//
// The web app is deployed to everyone the moment it ships, while installed
// desktop apps update over days and browser users never get desktop features.
// So every desktop-only behaviour asks the preload bridge what exists:
//
//   window.electronAPI.capabilities = { timerBar: true, nativeReminders: true }
//
// is exposed only by desktop 1.0.13+.  On 1.0.12 and in a browser it is
// undefined, every helper here answers "no", and the page behaves exactly as
// before (in-page ⏱ button, Calendar's own desktop notification).

import { useEffect, useState } from 'react'

const api = () => (typeof window !== 'undefined' ? window.electronAPI : undefined)

// The floating timer bar exists AND its control surface is present.  Both are
// required: a capability flag without the API object would leave the ⏱ button
// hidden with nothing to replace it.
export function hasTimerBar () {
  const e = api()
  return !!(e && e.capabilities && e.capabilities.timerBar === true && e.timerBar &&
    typeof e.timerBar.getState === 'function')
}

// The desktop app's main process raises reminder notifications itself.
export function hasNativeReminders () {
  const e = api()
  return !!(e && e.capabilities && e.capabilities.nativeReminders === true)
}

// The desktop app can hand one of the app's own PDFs to the computer's PDF
// program (Edge / Acrobat on Windows, Preview on Mac), whose print button and
// preview work — the PDF window older apps open cannot print (an Electron
// limitation, electron/electron#26448).  Desktop 1.0.16+ (2026-11-01):
//   electronAPI.capabilities.openPdf === true
//   electronAPI.openPdf({ data: Uint8Array, filename }) → Promise<{ ok, error? }>
// Both are required, as for the timer bar.  `e` defaults to window.electronAPI.
export function hasDesktopOpenPdf (e = api()) {
  return !!(e && e.capabilities && e.capabilities.openPdf === true && typeof e.openPdf === 'function')
}

// The notifier the Calendar page should hand useReminderNotifications.
// Desktop 1.0.13's tray poller notifies for every reminder occurrence even
// while the window is hidden; if the page ALSO notified, every reminder would
// pop up twice whenever the Calendar happened to be open.  On 1.0.12 the
// poller never authenticated (it read the token from sessionStorage), so the
// page keeps doing it there — and in a browser there is nothing to call.
export function inPageReminderNotifier (notify) {
  const e = api()
  if (!e || !e.isElectron) return null
  if (hasNativeReminders()) return null
  return typeof notify === 'function' ? notify : null
}

// Does the floating bar REPLACE the in-page ⏱ button right now?  Only when the
// bar exists and the person has it switched on — someone who hides the bar
// gets the in-page button back, so there is always a way to start a timer.
export function timerBarReplacesFab (state) {
  return !!(state && state.supported && state.visible)
}

// { supported, visible, alwaysOnTop, loaded } for the timer bar, live.
//
// Before getState() answers, `visible` assumes the desktop default (on), so
// the ⏱ button does not flash on and off at every page load.  If getState()
// fails the bar is treated as hidden and the button comes back — a broken
// bar must never leave someone without a timer launcher.
export function useTimerBarState () {
  const supported = hasTimerBar()
  const [state, setState] = useState(() => ({
    supported,
    visible: supported,
    alwaysOnTop: supported,
    loaded: !supported
  }))
  useEffect(() => {
    if (!supported) return undefined
    const bar = window.electronAPI.timerBar
    let alive = true
    const apply = (s) => {
      if (!alive || !s || typeof s !== 'object') return
      setState({ supported: true, visible: s.visible === true, alwaysOnTop: s.alwaysOnTop !== false, loaded: true })
    }
    let unsubscribe = null
    try {
      if (typeof bar.onStateChanged === 'function') unsubscribe = bar.onStateChanged(apply)
    } catch { unsubscribe = null }
    try {
      Promise.resolve(bar.getState()).then(apply, () => {
        if (alive) setState({ supported: true, visible: false, alwaysOnTop: true, loaded: true })
      })
    } catch {
      setState({ supported: true, visible: false, alwaysOnTop: true, loaded: true })
    }
    return () => {
      alive = false
      if (typeof unsubscribe === 'function') { try { unsubscribe() } catch {} }
    }
  }, [supported])
  return supported ? state : { supported: false, visible: false, alwaysOnTop: false, loaded: true }
}
